import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import Icon from '../Shared/Icons';
import AnnouncementsWidget from '../Shared/AnnouncementsWidget';
import { getProgram, MEMBER_TYPES } from '../../config/programs';
import { fetchMembers, fetchClubs, fetchReports } from '../../utils/programData';
import { ensureMsaadaMembers } from '../../utils/autoSync';
import { isoWeekKey, weekLabel, daysLeftInWeek, fmtDate } from '../../utils/weekUtils';
import { ProgramShell, S, T } from './ui';

export default function ProgramDashboard() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const program = getProgram(pid);
  const [data, setData] = useState(null);
  const wk = isoWeekKey();

  useEffect(() => { if (program) load(); /* eslint-disable-next-line */ }, [pid]);

  async function load() {
    try {
      await ensureMsaadaMembers();
      const [members, clubs, indiv, sessions] = await Promise.all([
        fetchMembers(pid),
        fetchClubs(pid),
        fetchReports({ programId: pid, kind: 'individual', weekKey: wk }),
        fetchReports({ programId: pid, kind: 'session' }),
      ]);
      const byType = {};
      members.forEach(m => { byType[m.type] = (byType[m.type] || 0) + 1; });
      setData({ members, clubs, indiv, sessions, byType });
    } catch (err) { console.error(err); }
  }

  if (!program) return <Navigate to="/programs" />;

  const done = data?.indiv.length || 0;
  const total = data?.members.length || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <ProgramShell program={program} tab="home">

      {/* Ukamilifu wa ripoti za wiki hii */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '13.5px', fontWeight: 800, color: T.green }}>Ripoti za Wiki Hii</div>
            <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '3px' }}>{weekLabel()}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '22px', fontWeight: 800, color: pct === 100 ? '#059669' : program.color, lineHeight: 1 }}>
              {done}/{total}
            </div>
            <div style={{ fontSize: '10.5px', color: T.muted, marginTop: '3px' }}>
              {daysLeftInWeek()} siku zimebaki
            </div>
          </div>
        </div>

        <div style={{ height: '9px', background: '#F0EEE7', borderRadius: '99px', marginTop: '14px', overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: '100%', borderRadius: '99px',
            background: pct === 100 ? '#059669' : program.color, transition: 'width .3s',
          }} />
        </div>

        <button
          onClick={() => navigate(`/programs/${pid}/week`)}
          style={{ ...S.btn, marginTop: '16px', width: '100%', background: program.color }}
        >
          Fungua Bodi ya Wiki
        </button>
      </div>

      <AnnouncementsWidget limitTo={3} programId={pid} />

      {/* Takwimu */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '11px', marginBottom: '16px' }}>
        {program.memberTypes.map(t => (
          <Tile
            key={t}
            value={data?.byType[t] || 0}
            label={MEMBER_TYPES[t]?.plural || t}
            icon={t === 'mzazi' || t === 'mtu_mzima' ? 'parents' : 'students'}
            color={program.color}
            onClick={() => navigate(`/programs/${pid}/members?type=${t}`)}
          />
        ))}
        <Tile value={data?.clubs.length || 0} label="Vilabu" icon="chess" color="#00695C"
          onClick={() => navigate(`/programs/${pid}/clubs`)} />
        <Tile value={data?.sessions.length || 0} label="Ripoti za session" icon="calendar" color="#4527A0"
          onClick={() => navigate(`/programs/${pid}/session`)} />
      </div>

      {/* Vitendo vya haraka */}
      <div style={S.card}>
        <h2 style={S.h2}>Vitendo vya Haraka</h2>
        <Action icon="plus" label="Sajili mwanachama mpya" color={program.color}
          onClick={() => navigate(`/programs/${pid}/members/new`)} />
        <Action icon="report" label="Andika ripoti binafsi ya wiki" color="#4527A0"
          onClick={() => navigate(`/programs/${pid}/week`)} />
        <Action icon="calendar" label="Ripoti ya session / tukio" color="#00695C"
          onClick={() => navigate(`/programs/${pid}/session?new=1`)} />
        <Action icon="ai" label="Uchambuzi wa AI" color="#E07A2E"
          onClick={() => navigate(`/programs/${pid}/ai`)} last />
      </div>

      {/* Ripoti za session za karibuni */}
      <div style={S.card}>
        <h2 style={S.h2}>Session za Karibuni</h2>
        {!data?.sessions.length ? (
          <div style={S.empty}>Bado hakuna ripoti ya session kwenye programu hii.</div>
        ) : data.sessions.slice(0, 5).map(s => (
          <div
            key={s.id}
            onClick={() => navigate(`/programs/${pid}/session?open=${s.id}`)}
            style={{
              padding: '11px 0', borderTop: `1px solid #F3F1EB`, cursor: 'pointer',
              display: 'flex', justifyContent: 'space-between', gap: '10px',
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '13.5px', fontWeight: 700, color: T.ink }}>
                {s.values?.topicChess || s.clubName || 'Session'}
              </div>
              <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '3px' }}>
                {s.clubName} · {s.date || fmtDate(s.createdAt)} · {s.authorName}
              </div>
            </div>
            <div style={{ fontSize: '11.5px', color: T.muted, whiteSpace: 'nowrap' }}>
              {s.presentCount ?? '—'} walihudhuria
            </div>
          </div>
        ))}
      </div>

    </ProgramShell>
  );
}

function Tile({ value, label, icon, color, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: '#fff', border: `1px solid ${T.line}`, borderRadius: '14px',
      padding: '15px', cursor: 'pointer',
    }}>
      <Icon name={icon} size={17} color={color} />
      <div style={{ fontSize: '22px', fontWeight: 800, color: T.ink, marginTop: '9px', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '4px' }}>{label}</div>
    </div>
  );
}

function Action({ icon, label, color, onClick, last }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 0',
      borderBottom: last ? 'none' : `1px solid #F3F1EB`, cursor: 'pointer',
    }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '9px', background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon name={icon} size={15} color={color} />
      </div>
      <div style={{ flex: 1, fontSize: '13.5px', fontWeight: 600, color: T.ink }}>{label}</div>
      <span style={{ color: T.muted }}>›</span>
    </div>
  );
}
